// Apolo IDE — @file/@folder/@docs/@symbol mention parser + resolver (Sprint v11 E2).
//
// User writes `@file:src/App.jsx` in the composer. We parse those tokens
// out, fetch each one's content (via Electron file IPC for files/folders,
// fetch() for @docs, future semantic-search for @symbol), and wrap the
// content into <context> blocks the model can read. The visible composer
// text is rendered with chip placeholders so the user sees a clean prompt.
//
// Token budget: hard-capped at MAX_TOTAL_CONTEXT_CHARS so a single prompt
// can't blow past the model context window. Per-mention contribution is
// also capped (MAX_FILE_CHARS, MAX_FOLDER_FILES, etc.).

// `node` = a Memory v2 graph node (`@node:file:src/App.jsx`). Reuses the
// existing @mention path — NO new slash command (Pedro's two-plugin gotcha).
//
// Recuperação Aba-Apolo C2 — `#chat:<sessionId>` (contexto de CONVERSA PASSADA,
// estilo Copilot). `@` = arquivos/símbolos; `#` = contexto de nível mais alto.
// Fica numa alternativa separada do regex pra `@` e `#` não se misturarem.
const MENTION_REGEX = /@(file|folder|docs|symbol|node):([^\s@<>'"]+)|#(chat):([^\s#<>'"]+)/g;
const MAX_NODE_CHARS = 4000;
const MAX_TOTAL_CONTEXT_CHARS = 60_000;
const MAX_FILE_CHARS = 20_000;
const MAX_FOLDER_FILES = 5;
const MAX_FOLDER_CHARS = 30_000;
const MAX_DOC_CHARS = 15_000;
const MAX_CHAT_CHARS = 15_000;

/**
 * Parse mentions out of a string. Returns the list of mentions in the
 * order they appear (with their match start/end indices) so callers can
 * render chip placeholders inline.
 *
 * @param {string} text
 * @returns {{ type: 'file'|'folder'|'docs'|'symbol', target: string, start: number, end: number, raw: string }[]}
 */
export function parseMentions(text) {
  if (typeof text !== 'string' || !text) return [];
  const out = [];
  MENTION_REGEX.lastIndex = 0;
  let m;
  while ((m = MENTION_REGEX.exec(text)) !== null) {
    // Alternativa @ (grupos 1/2) OU #chat (grupos 3/4).
    out.push({
      type: m[1] || m[3],
      target: m[2] || m[4],
      start: m.index,
      end: m.index + m[0].length,
      raw: m[0],
    });
  }
  return out;
}

/**
 * Split a string into alternating text + mention nodes, in document order.
 * Useful for rendering chips inline with the surrounding text.
 *
 * @param {string} text
 * @returns {Array<{kind:'text', value:string} | {kind:'mention', value: ReturnType<typeof parseMentions>[number]}>}
 */
export function tokenizeForRender(text) {
  const mentions = parseMentions(text);
  if (mentions.length === 0) return text ? [{ kind: 'text', value: text }] : [];
  const out = [];
  let cursor = 0;
  for (const m of mentions) {
    if (m.start > cursor) {
      out.push({ kind: 'text', value: text.slice(cursor, m.start) });
    }
    out.push({ kind: 'mention', value: m });
    cursor = m.end;
  }
  if (cursor < text.length) {
    out.push({ kind: 'text', value: text.slice(cursor) });
  }
  return out;
}

function clip(text, max) {
  if (typeof text !== 'string') return '';
  if (text.length <= max) return text;
  return text.slice(0, max) + `\n\n… [truncated ${text.length - max} chars]`;
}

/**
 * Resolve a single mention using the supplied IO functions. Returns the
 * raw resolved content (plain text) or null when the resolver is missing
 * or fails. Errors are swallowed — the caller decides what to do.
 *
 * @param {{ type: string, target: string }} mention
 * @param {{
 *   readFile: (path: string) => Promise<string | null>,
 *   listFolder: (path: string) => Promise<Array<{ path: string, name: string, isDirectory: boolean }>>,
 *   fetchUrl: (url: string) => Promise<string | null>,
 *   grepSymbol: (name: string) => Promise<string | null>,
 *   resolveGraphNode?: (nodeId: string) => Promise<string | null>,
 *   loadChat?: (sessionId: string) => Promise<string | null>,
 * }} io
 * @returns {Promise<string | null>}
 */
export async function resolveMention(mention, io) {
  if (!mention || typeof mention.target !== 'string') return null;
  try {
    if (mention.type === 'file') {
      if (typeof io?.readFile !== 'function') return null;
      const content = await io.readFile(mention.target);
      return content ? clip(content, MAX_FILE_CHARS) : null;
    }
    if (mention.type === 'folder') {
      if (typeof io?.listFolder !== 'function' || typeof io?.readFile !== 'function') return null;
      const entries = await io.listFolder(mention.target);
      if (!Array.isArray(entries) || entries.length === 0) return null;
      const files = entries.filter((e) => !e.isDirectory).slice(0, MAX_FOLDER_FILES);
      const tree = entries
        .slice(0, 50)
        .map((e) => (e.isDirectory ? `📁 ${e.path}/` : `📄 ${e.path}`))
        .join('\n');
      const fileBlocks = [];
      let budget = MAX_FOLDER_CHARS;
      for (const f of files) {
        if (budget <= 0) break;
        const content = await io.readFile(f.path);
        if (!content) continue;
        const clipped = clip(content, Math.min(budget, MAX_FILE_CHARS));
        fileBlocks.push(`### ${f.path}\n\`\`\`\n${clipped}\n\`\`\``);
        budget -= clipped.length;
      }
      return `Tree:\n${tree}\n\n${fileBlocks.join('\n\n')}`.trim();
    }
    if (mention.type === 'docs') {
      if (typeof io?.fetchUrl !== 'function') return null;
      const text = await io.fetchUrl(mention.target);
      return text ? clip(text, MAX_DOC_CHARS) : null;
    }
    if (mention.type === 'symbol') {
      if (typeof io?.grepSymbol !== 'function') return null;
      const text = await io.grepSymbol(mention.target);
      return text ? clip(text, MAX_FILE_CHARS) : null;
    }
    if (mention.type === 'node') {
      // Memory v2 (MAP2): inject the node's structural subgraph via
      // /api/memory/retrieve — already InjectionBudget-bounded server-side.
      if (typeof io?.resolveGraphNode !== 'function') return null;
      const text = await io.resolveGraphNode(mention.target);
      return text ? clip(text, MAX_NODE_CHARS) : null;
    }
    if (mention.type === 'chat') {
      // Aba-Apolo C2 — `#chat:<sessionId>`: injeta a TRANSCRIÇÃO de uma conversa
      // passada como contexto (read-only). io.loadChat busca as mensagens da
      // sessão e formata. Referência é read-only — NÃO mistura as sessões.
      if (typeof io?.loadChat !== 'function') return null;
      const text = await io.loadChat(mention.target);
      return text ? clip(text, MAX_CHAT_CHARS) : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolve every mention and return:
 *   - `enrichedText`: the original text with each mention rewritten to
 *     a short placeholder (`[@file:src/App.jsx]`) followed by a
 *     `<context>` block at the bottom of the prompt
 *   - `mentions`: the parse result + resolved status per entry
 *
 * @param {string} text
 * @param {Parameters<typeof resolveMention>[1]} io
 * @returns {Promise<{ enrichedText: string, mentions: Array<{ type:string, target:string, raw:string, resolved:boolean }> }>}
 */
export async function resolveAndEnrich(text, io) {
  const mentions = parseMentions(text);
  if (mentions.length === 0) {
    return { enrichedText: text, mentions: [] };
  }
  const resolved = await Promise.all(
    mentions.map(async (m) => {
      const content = await resolveMention(m, io);
      return { ...m, content, resolved: !!content };
    }),
  );

  // Replace mention spans with simple placeholders so the prompt body is
  // readable but the LLM still knows where each context block belongs.
  let body = '';
  let cursor = 0;
  for (const m of resolved) {
    body += text.slice(cursor, m.start);
    // Usa o token ORIGINAL (m.raw) — preserva o prefixo (@ ou #). Byte-idêntico
    // pros @ (@file:x → [@file:x]) e correto pro #chat (#chat:id → [#chat:id]).
    body += `[${m.raw}]`;
    cursor = m.end;
  }
  body += text.slice(cursor);

  // Append context blocks, capped by the total budget.
  const blocks = [];
  let budget = MAX_TOTAL_CONTEXT_CHARS;
  for (const m of resolved) {
    if (!m.content) continue;
    const block = `<context type="${m.type}" target="${m.target}">\n${m.content}\n</context>`;
    if (block.length > budget) break;
    blocks.push(block);
    budget -= block.length;
  }
  const enrichedText = blocks.length === 0 ? body : `${body}\n\n${blocks.join('\n\n')}`;
  return {
    enrichedText,
    mentions: resolved.map(({ content: _content, ...rest }) => rest),
  };
}
